import { apiFetch, httpBlob, httpJson, resolveEndpoint } from "./client";

function fallbackJobId() {
  const stamp = Date.now().toString(36);
  return `job_${stamp}`;
}

function firstString(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function resolveCode(row, platform) {
  const generic = firstString(row.platform_code, row.item_code, row.code, row.canonical_key, row.canonical_task_id);
  if (platform === "symbility") {
    return firstString(row.symbility_code) || (generic ? (generic.startsWith("SYM_") ? generic : `SYM_${generic}`) : "");
  }
  return firstString(row.xactimate_code) || generic;
}

async function resolvePath(preferred, fallback = "") {
  try {
    const found = await resolveEndpoint(preferred);
    if (found) return found;
  } catch (e) {
    // fall through
  }
  return fallback;
}

export async function ensureJob(existingJobId = "") {
  if (existingJobId && String(existingJobId).trim()) {
    return { jobId: String(existingJobId).trim(), created: false };
  }

  try {
    const data = await httpJson("/jobs", {
      method: "POST",
      body: { name: "Review Wizard Job" },
    });
    const jobId = firstString(data?.id, data?.job_id, data?.jobId);
    if (!jobId) throw new Error("Missing job id in response");
    return { jobId, created: true };
  } catch (e) {
    return { jobId: fallbackJobId(), created: false, fallback: true };
  }
}

export async function runReviewFromEstimate({ jobId, file, platform, profileId = "default" }) {
  if (!jobId) throw new Error("runReviewFromEstimate requires jobId");
  if (!file) throw new Error("runReviewFromEstimate requires estimate file");

  const pathTemplate = await resolvePath(
    ["/suggestions/jobs/{job_id}/run-from-estimate"],
    "/suggestions/jobs/{job_id}/run-from-estimate"
  );
  const query = new URLSearchParams({
    platform: platform || "xactimate",
    profile_id: profileId || "default",
  }).toString();
  const path = `${pathTemplate.replace("{job_id}", encodeURIComponent(jobId))}?${query}`;

  const form = new FormData();
  form.append("file", file);

  const { data } = await apiFetch(path, {
    method: "POST",
    body: form,
  });

  return {
    jobId,
    setId: firstString(data?.set_id, data?.setId, data?.id),
    payload: data,
  };
}

export async function getLatestSuggestionSet(jobId, platform = "xactimate") {
  if (!jobId) throw new Error("getLatestSuggestionSet requires jobId");
  const pathTemplate = await resolvePath(
    ["/suggestions/jobs/{job_id}/latest"],
    "/suggestions/jobs/{job_id}/latest"
  );
  const query = new URLSearchParams({ platform: platform || "xactimate" }).toString();
  const path = `${pathTemplate.replace("{job_id}", encodeURIComponent(jobId))}?${query}`;
  const data = await httpJson(path, { method: "GET" });
  return {
    setId: firstString(data?.set_id, data?.setId, data?.id),
    payload: data,
  };
}

export function normalizeSuggestionSetToRows(setPayload, displayPlatform = "xactimate") {
  const items =
    (Array.isArray(setPayload?.items) && setPayload.items) ||
    (Array.isArray(setPayload?.suggestions) && setPayload.suggestions) ||
    [];

  return items.map((item) => {
    const qtyNum = Number(item.qty ?? item.quantity);
    const confidenceNum = Number(item.confidence);
    return {
      room: firstString(item.room, item.room_name) || "(unknown)",
      code: resolveCode(item, displayPlatform),
      description: firstString(item.description, item.label, item.name) || "Suggested line item",
      qty: Number.isFinite(qtyNum) ? qtyNum : 1,
      unit: firstString(item.unit) || "EA",
      confidence: Number.isFinite(confidenceNum) ? confidenceNum : null,
    };
  });
}

export async function downloadEstimatorPackZip({ jobId, setId, platform = "xactimate", includeAll = true, includeRationale = true }) {
  const setPathTemplate = await resolvePath(
    ["/suggestions/sets/{set_id}/estimator-pack.zip"],
    "/suggestions/sets/{set_id}/estimator-pack.zip"
  );
  const jobPathTemplate = await resolvePath(
    ["/suggestions/jobs/{job_id}/estimator-pack.zip"],
    "/suggestions/jobs/{job_id}/estimator-pack.zip"
  );

  const query = new URLSearchParams({
    platform,
    include_all: includeAll ? "true" : "false",
    include_rationale: includeRationale ? "true" : "false",
    optimize_entry_order: "1",
  }).toString();

  if (setId) {
    const setPath = `${setPathTemplate.replace("{set_id}", encodeURIComponent(setId))}?${query}`;
    try {
      return await httpBlob(setPath, { method: "GET" });
    } catch (e) {
      if (!jobId) throw e;
    }
  }

  if (!jobId) throw new Error("downloadEstimatorPackZip requires setId or jobId");
  const jobPath = `${jobPathTemplate.replace("{job_id}", encodeURIComponent(jobId))}?${query}`;
  return await httpBlob(jobPath, { method: "GET" });
}

export const DEFAULT_REVIEW_SUGGESTIONS = [
  {
    room: "Main Level",
    code: "PNT_PRM_PAINT",
    description: "Prime and paint wall/ceiling surfaces",
    qty: 120,
    unit: "SF",
    confidence: 0.9,
  },
  {
    room: "Family Room",
    code: "FLR_TILE_RR",
    description: "Tile floor remove/replace",
    qty: 34,
    unit: "SF",
    confidence: 0.85,
  },
  {
    room: "Up 3'",
    code: "LAB_MIN_VINYL",
    description: "Labor minimum - vinyl flooring",
    qty: 1,
    unit: "EA",
    confidence: 0.8,
  },
];
